/* eslint-disable new-cap */
/**
 * Sender Email API hooks for managing sender email addresses
 * Calls Python API endpoints at strix-api /api/sender-emails
 */
import { useCallback, useEffect, useState } from "react";
import { restClient } from "../lib/rest-client";

// =====================================
// Types
// =====================================

/** 送信者メールアイテム */
export type SenderEmailItem = {
  id: string;
  email: string;
  displayName: string;
  verificationStatus: "pending" | "success" | "failed";
  verifiedAt: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

/** 送信者メール一覧レスポンス */
export type SenderEmailListResponse = {
  items: SenderEmailItem[];
  total: number;
};

/** 送信者メール作成リクエスト */
export type CreateSenderEmailInput = {
  email: string;
  displayName: string;
};

/** 送信者メール更新リクエスト */
export type UpdateSenderEmailInput = {
  displayName?: string;
  isDefault?: boolean;
};

// =====================================
// Hooks
// =====================================

export type UseSenderEmailsOptions = {
  verifiedOnly?: boolean;
};

export type UseSenderEmailsResult = {
  data: SenderEmailListResponse | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<SenderEmailListResponse>;
};

/**
 * 送信者メール一覧取得
 * @param {UseSenderEmailsOptions} options オプション
 * @return {UseSenderEmailsResult} 送信者メール一覧データとローディング状態
 */
export function useSenderEmails(
  options: UseSenderEmailsOptions = {},
): UseSenderEmailsResult {
  const [data, setData] = useState<SenderEmailListResponse | undefined>(
    undefined,
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchSenderEmails =
    useCallback(async (): Promise<SenderEmailListResponse> => {
      setLoading(true);
      setError(undefined);

      try {
        const params: Record<string, string> = {};
        if (options.verifiedOnly) {
          params.verified_only = "true";
        }

        const responseData = await restClient.get<SenderEmailListResponse>(
          "/sender-emails",
          Object.keys(params).length > 0 ? params : undefined,
        );
        setData(responseData);
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    }, [options.verifiedOnly]);

  useEffect(() => {
    void fetchSenderEmails();
    // eslint-disable-next-line @typescript-eslint/no-empty-function
  }, [fetchSenderEmails]);

  return { data, error, loading, refetch: fetchSenderEmails };
}

/**
 * 送信者メール登録
 * @return {object} 作成関数とローディング状態
 */
export function useCreateSenderEmail() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createSenderEmail = useCallback(
    async (input: CreateSenderEmailInput): Promise<SenderEmailItem> => {
      setLoading(true);
      setError(undefined);

      try {
        return await restClient.post<SenderEmailItem>("/sender-emails", input);
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { createSenderEmail, loading, error };
}

/**
 * 送信者メール更新
 * @return {object} 更新関数とローディング状態
 */
export function useUpdateSenderEmail() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateSenderEmail = useCallback(
    async (
      id: string,
      input: UpdateSenderEmailInput,
    ): Promise<SenderEmailItem> => {
      setLoading(true);
      setError(undefined);

      try {
        return await restClient.put<SenderEmailItem>(
          `/sender-emails/${id}`,
          input,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { updateSenderEmail, loading, error };
}

/**
 * 送信者メール削除
 * @return {object} 削除関数とローディング状態
 */
export function useDeleteSenderEmail() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const deleteSenderEmail = useCallback(async (id: string): Promise<void> => {
    setLoading(true);
    setError(undefined);

    try {
      await restClient.delete<void>(`/sender-emails/${id}`);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteSenderEmail, loading, error };
}

/**
 * 認証メール再送
 * @return {object} 再送関数とローディング状態
 */
export function useResendVerification() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const resendVerification = useCallback(
    async (id: string): Promise<SenderEmailItem> => {
      setLoading(true);
      setError(undefined);

      try {
        return await restClient.post<SenderEmailItem>(
          `/sender-emails/${id}/resend-verification`,
        );
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { resendVerification, loading, error };
}
